import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from './service/login.service';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventMessage, EventType, AuthenticationResult } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { GetAllocationMarketDataService } from './service/get-allocation-market-data.service';
import { WholesaleConstants } from './constants/wholesale.constants';

@Component({
	standalone: false,
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
	hamMenu: boolean;
	showComponent: boolean;
	loggedInUser: string;
	loggedInUserMarketText: string;
	year: number;
	displayVersion = '13.0';
	private readonly _destroying$ = new Subject<void>();

	constructor(
		private translate: TranslateService,
		private loginService: LoginService,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService,
		private getAllocationMarketDataService: GetAllocationMarketDataService
	) {
		this.year = new Date().getFullYear();
	}

	ngOnInit(): void {
		// Handle MSAL redirect - needed when MsalGuard redirects back to a protected route
		this.msalService.handleRedirectObservable().subscribe({
			next: (result: AuthenticationResult) => {
				if (result && result.accessToken) {
					this.initializeSessionFromMsal(result);
				} else {
					// No result from redirect but check if we have cached accounts
					this.checkSessionAndInitialize();
				}
			},
			error: (error) => {
				console.error('MSAL redirect error:', error);
			}
		});

		// Listen for login success events
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
				takeUntil(this._destroying$)
			)
			.subscribe((result: EventMessage) => {
				const payload = result.payload as AuthenticationResult;
				if (payload && payload.accessToken && !sessionStorage.getItem('cdsId')) {
					this.initializeSessionFromMsal(payload);
				}
			});

		// Subscribe to login service - session is managed by oauth-callback components
		this.loginService.isLoggedIn$().subscribe(isLoggedIn => {
			if (isLoggedIn) {
				setTimeout(() => this.updateComponentState(), 100);
			}
		});

		// Update market display when user selects a different market
		this.loginService.marketChanged
			.pipe(takeUntil(this._destroying$))
			.subscribe(marketText => {
				this.loggedInUserMarketText = marketText;
			});


		// Wait for MSAL interaction to complete, then check session state
		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.checkSessionAndInitialize();
			});

		// Initial check on startup - if session already exists, update UI immediately
		if (sessionStorage.getItem('cdsId') && sessionStorage.getItem('loggedInUserMarketText')) {
			this.updateComponentState();
		}
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	/**
	 * Check if user is logged in but session not initialized, then initialize
	 */
	private checkSessionAndInitialize(): void {
		const accounts = this.msalService.instance.getAllAccounts();

		if (accounts.length > 0) {
			if (sessionStorage.getItem('cdsId') && sessionStorage.getItem('loggedInUserMarketText')) {
				// Session already initialized, just update UI
				this.updateComponentState();
			} else if (!sessionStorage.getItem('authToken')) {
				// User logged in but no session - acquire token and initialize
				this.acquireTokenAndInitialize();
			}
		}
	}

	private async acquireTokenAndInitialize(): Promise<void> {
		const accounts = this.msalService.instance.getAllAccounts();
		if (accounts.length === 0) return;

		const account = accounts[0];
		this.msalService.instance.setActiveAccount(account);

		try {
			const result = await this.msalService.instance.acquireTokenSilent({
				scopes: environment.apiConfig.scopes,
				account: account
			});
			await this.initializeSessionFromMsal(result);
		} catch (error) {
			console.error('Silent token acquisition failed:', error);
		}
	}

	private async initializeSessionFromMsal(authResult: AuthenticationResult): Promise<void> {
		if (!authResult || !authResult.accessToken) return;
		if (sessionStorage.getItem('cdsId')) return; // Already initialized

		const accessToken = authResult.accessToken;
		const idTokenClaims = authResult.idTokenClaims as any;
		const account = authResult.account;

		// Calculate token expiration
		const expiration = authResult.expiresOn
			? Math.trunc(authResult.expiresOn.getTime() / 1000)
			: Math.trunc(new Date().getTime() / 1000) + 3600;

		// Set base URLs
		sessionStorage.setItem('baseCommitmentUrl', environment.baseUrl + environment.commitmentUrl);
		sessionStorage.setItem('baseDealerServiceUrl', environment.baseUrl + environment.dealerServiceUrl);
		sessionStorage.setItem('baseAllocationUrl', environment.baseUrl + environment.wholesaleAllocationUrl);
		sessionStorage.setItem('baseBulkOrderCreationUrl', environment.baseUrl + environment.baseBulkOrderCreationUrl);
		sessionStorage.setItem('baseWersWholesaleAllocationUrl', environment.baseUrl + environment.wersWholesaleAllocationUrl);

		// Store tokens
		sessionStorage.setItem('encodedIdToken', authResult.idToken || '');
		sessionStorage.setItem('encodedAccessToken', accessToken);
		sessionStorage.setItem('authToken', accessToken);
		sessionStorage.setItem('tokenExp', String(expiration));

		// Extract user info
		const cdsId = this.extractCdsId(idTokenClaims, account);
		const countryCode = this.extractCountryCode(idTokenClaims);
		const siteCode = this.extractSiteCode(idTokenClaims);

		sessionStorage.setItem('cdsId', cdsId.toUpperCase());
		sessionStorage.setItem('loggedInSiteCode', siteCode);

		// Fetch roles and market data
		try {
			const getRolesFromApsUrl = sessionStorage.getItem('baseDealerServiceUrl') + WholesaleConstants.getApsRolesUrl;
			const rolesResponse = await this.loginService.getApsRoles(getRolesFromApsUrl);
			const roles: string[] = [];
			if (rolesResponse && rolesResponse['RoleAssignment']) {
				rolesResponse['RoleAssignment'].forEach(role => roles.push(role.RoleName));
			}

			// Assign roleCode
			if (roles.includes('Zone Manager') || roles.includes('NSC')) {
				sessionStorage.setItem('roleCode', '1');
			} else if (roles.includes('Dealer Group')) {
				sessionStorage.setItem('roleCode', '3');
			} else if (roles.includes('CMA Dealer')) {
				sessionStorage.setItem('roleCode', '2');
			} else if (roles.includes('Product Team')) {
				sessionStorage.setItem('roleCode', '4');
			} else {
				sessionStorage.setItem('roleCode', '5');
			}

			// Fetch market data
			const marketLanguageMapping: any[] = await this.getAllocationMarketDataService.getMarketLanguageMappingList().toPromise();
			const filteredMarkets = marketLanguageMapping?.filter(market => market.dealerMarket === countryCode) || [];

			if (filteredMarkets.length > 0) {
				const marketInfo = filteredMarkets[0].marketInfo;
				sessionStorage.setItem('loggedInUserMarketCode', marketInfo.marketCode);
				sessionStorage.setItem('loggedInUserMarketText', marketInfo.marketText);
				sessionStorage.setItem('loggedInUserLanguageCode', marketInfo.locale || 'en_GB');
			} else {
				sessionStorage.setItem('loggedInUserMarketCode', 'WAEGB');
				sessionStorage.setItem('loggedInUserMarketText', 'Ford of Britain');
				sessionStorage.setItem('loggedInUserLanguageCode', 'en_GB');
			}
		} catch (err) {
			console.error('Error fetching roles/market data:', err);
			sessionStorage.setItem('roleCode', '5');
			sessionStorage.setItem('loggedInUserMarketCode', 'WAEGB');
			sessionStorage.setItem('loggedInUserMarketText', 'Ford of Britain');
			sessionStorage.setItem('loggedInUserLanguageCode', 'en_GB');
		}

		this.loginService.isLoggedIn.next(true);
		this.updateComponentState();
	}

	private extractCdsId(idTokenClaims: any, account: any): string {
		return idTokenClaims?.CommonName ||
			idTokenClaims?.extension_CommonName ||
			idTokenClaims?.onprem_sam_account_name ||
			idTokenClaims?.preferred_username?.split('@')[0] ||
			account?.username?.split('@')[0] ||
			idTokenClaims?.name ||
			'unknown';
	}

	private extractCountryCode(idTokenClaims: any): string {
		return idTokenClaims?.c ||
			idTokenClaims?.ctry ||
			idTokenClaims?.country ||
			'GBR';
	}

	private extractSiteCode(idTokenClaims: any): string {
		const fordSiteCode = idTokenClaims?.forddealersitecode ||
			'';
		if (fordSiteCode && fordSiteCode.length >= 8) {
			return fordSiteCode.substring(3, 8);
		}
		return fordSiteCode || '';
	}

	/**
	 * Update component state from session storage.
	 * Called when login is successful and session variables are set.
	 */
	private updateComponentState(): void {
		const cdsId = sessionStorage.getItem('cdsId');
		if (cdsId) {
			this.showComponent = true;
			this.loggedInUser = cdsId;
			this.loggedInUserMarketText = sessionStorage.getItem('loggedInUserMarketText') || '';
			const languageCode = sessionStorage.getItem('loggedInUserLanguageCode');
			if (languageCode) {
				this.translate.use(languageCode);
			}
		}
	}

	@HostListener('window:resize', ['$event']) onResize(event?) {
		if (event?.target?.innerWidth > 767) {
			this.hamMenu = false;
		}
	}

	public logoutEvent() {
		this.loginService.logout().subscribe(response => {
		});
		this.showComponent = false;
		sessionStorage.clear();
		this.msalService.logout();
	}
}
